import { IconButton, SxProps, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import { useState } from 'react';
import { downloadCSV, generateFilename } from '../helpers/download';
import { UserContext } from '../../../stores/User/User.provider';
import useStore from '../../../helpers/useStore';
import useSendToEmail from '../hooks/useSendToEmail';

interface ColumnNameConfig<T> {
  header: string;
  field: keyof T; 
  format?: (val: T[keyof T]) => string | number;
  csvFormat?: (val: T[keyof T]) => string; 
};

interface ExportButtonProps<T> {
  data: T[];
  title: string;
  columns: ColumnNameConfig<T>[];
  startDate?: Date;
  endDate?: Date;
  year?: number;
  disabled?: boolean;
  sx?: SxProps;
}

const ExportButton = <T,>({data, title, columns, startDate, endDate, year, disabled, sx}: ExportButtonProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState(`Controlling Report: ${title}`);
  
  const open = Boolean(anchorEl);
  const UserStore = useStore(UserContext);
  const { sendEmail, isLoading: emailLoading } = useSendToEmail();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = () => {
    if (data.length === 0) {
      console.warn('No data to download');
      return;
    }

    const filename = generateFilename(title, startDate, endDate, year);
    downloadCSV(data, columns, filename);
    handleClose();
  };

  const handleEmailClick = () => {
    if (data.length === 0) {
      console.warn('No data to send');
      return;
    }
    setEmailDialogOpen(true);
    handleClose();
  };

  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setRecipientEmail('');
    setEmailSubject(`Controlling Report: ${title}`);
  };

  const handleSendEmail = async () => {
    if (!recipientEmail.trim()) {
      return;
    }

    const filename = generateFilename(title, startDate, endDate, year);
    const csvData = downloadCSV(data, columns, filename, true) as string;

    const result = await sendEmail({
      csvData,
      filename,
      subject: emailSubject,
      recipientEmail: recipientEmail.trim()
    });

    if (result?.success) {
      handleEmailDialogClose();
    }
  };

  return (
    <>
      <Tooltip title="Export-Optionen" placement="top">
        <IconButton
          onClick={handleClick}
          sx={{ 
            padding: 0,
            ...sx 
          }}
          size="small"
          disabled={disabled}
          aria-controls={open ? 'export-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'export-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Als CSV herunterladen</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEmailClick}>
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Per E-Mail senden</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog 
        open={emailDialogOpen} 
        onClose={handleEmailDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>CSV per E-Mail senden</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="E-Mail-Adresse"
            type="email"
            fullWidth
            variant="outlined"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Betreff"
            fullWidth
            variant="outlined"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailDialogClose} disabled={emailLoading}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained"
            disabled={emailLoading || !recipientEmail.trim()}
            startIcon={emailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {emailLoading ? 'Senden...' : 'Senden'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExportButton;